import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, fmtKm, titleCase } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Spinner } from "@/components/common/Primitives";
import { StatusBadge } from "@/components/common/StatusBadge";
import { VehicleFormDialog } from "@/components/vehicles/VehicleFormDialog";
import { ComplianceTab } from "@/components/vehicles/ComplianceTab";
import { OdometerTab } from "@/components/vehicles/OdometerTab";
import { ServiceFormDialog, ServiceList } from "@/components/services/ServiceParts";
import { IssueFormDialog } from "@/components/issues/IssueFormDialog";
import { IssueList } from "@/components/issues/IssueList";
import { PhotosTab } from "@/components/photos/Photos";
import { ResourceSection } from "@/components/common/Resource";
import { consumables } from "@/lib/resources";
import { VehicleQR } from "@/components/vehicles/VehicleQR";
import { VehicleGallery } from "@/components/vehicles/VehicleGallery";
import { FuelTab, FuelGauge } from "@/components/vehicles/FuelTab";
import { GroupPicker } from "@/components/vehicles/GroupsDialog";
import { cn } from "@/lib/utils";

const TABS = ["overview", "compliance", "odometer", "fuel", "issues", "services", "photos", "consumables"];

export default function VehicleDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can } = useAuth();
  const [v, setV] = useState(null);
  const [tab, setTab] = useState("overview");
  const [edit, setEdit] = useState(false);
  const [issueOpen, setIssueOpen] = useState(false);
  const [svcOpen, setSvcOpen] = useState(false);

  const load = useCallback(() => api.get(`/vehicles/${id}`).then((r) => setV(r.data)).catch(() => navigate("/vehicles")), [id, navigate]);
  useEffect(() => { load(); }, [load]);
  if (!v) return <Spinner />;

  const remove = async () => {
    if (!window.confirm(`Delete ${v.registration_no} and all its records? This cannot be undone.`)) return;
    await api.delete(`/vehicles/${v.id}`); toast.success("Vehicle deleted"); navigate("/vehicles");
  };

  return (
    <div>
      <button onClick={() => navigate("/vehicles")} data-testid="back-to-vehicles" className="mb-3 flex items-center gap-1 text-sm font-medium text-primary hover:underline"><ArrowLeft className="h-4 w-4" />Vehicles</button>
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start">
        <VehicleGallery vehicle={v} className="h-52 w-full sm:h-40 sm:w-64" editable={can("admin", "fleet_manager", "garage_supervisor")} onChanged={load} />
        <div className="min-w-0 flex-1">
      <PageHeader title={`${v.make} ${v.model}`} subtitle={`${v.registration_no}${v.year ? ` · ${v.year}` : ""}${v.transmission ? ` · ${titleCase(v.transmission)}` : ""} · ${fmtKm(v.current_odometer_km)}`} testId="vehicle-detail-header"
        actions={<>
          {can("admin", "fleet_manager") && <GroupPicker vehicle={v} onChanged={load} />}
          <StatusBadge status={v.compliance.overall} testId="vehicle-compliance-overall" />
          <StatusBadge status={v.status} testId="vehicle-status" />
          {can("admin", "fleet_manager") && <button onClick={() => setEdit(true)} data-testid="edit-vehicle-btn" className="flex h-9 items-center gap-1 rounded-full border px-3 text-sm font-medium text-primary hover:bg-primary/10"><Pencil className="h-4 w-4" />Edit</button>}
          {can("admin") && <button onClick={remove} data-testid="delete-vehicle-btn" className="flex h-9 items-center rounded-lg border px-2 text-destructive hover:bg-destructive/10" aria-label="Delete vehicle"><Trash2 className="h-4 w-4" /></button>}
        </>} />
        </div>
      </div>
      <div className="mb-5 flex gap-1 overflow-x-auto rounded-full bg-muted p-1" role="tablist" data-testid="vehicle-tabs">
        {TABS.map((t) => (
          <button key={t} role="tab" aria-selected={tab === t} onClick={() => setTab(t)} data-testid={`vehicle-tab-${t}`} className={cn("flex-1 whitespace-nowrap rounded-full px-3 py-1.5 text-sm font-medium transition-colors", tab === t ? "bg-card text-primary border border-border" : "text-muted-foreground hover:text-foreground")}>
            {titleCase(t)}{t === "issues" && v.issues.filter((i) => i.status !== "resolved").length ? ` (${v.issues.filter((i) => i.status !== "resolved").length})` : ""}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="fv-card p-5" data-testid="vehicle-overview">
            <h2 className="mb-3 text-base font-semibold">Identity & spec</h2>
            <dl className="grid grid-cols-2 gap-y-3 text-sm">
              {[["Fuel", v.fuel_type?.toUpperCase()], ["Transmission", titleCase(v.transmission)], ["Type", titleCase(v.vehicle_type)], ["Group", v.group], ["Ownership", v.ownership], ["Current location", v.location], ["FASTag ID", v.fastag_id], ["VIN", v.vin], ["Engine no.", v.engine_no], ["Color", v.color]].map(([k, val]) => (
                <div key={k}><dt className="fv-caption">{k}</dt><dd className="mt-0.5 font-medium">{val || "—"}</dd></div>
              ))}
            </dl>
            {v.notes && <p className="mt-4 text-sm text-muted-foreground">{v.notes}</p>}
          </div>
          <div className="fv-card p-5">
            <h2 className="mb-3 text-base font-semibold">Compliance snapshot</h2>
            <ul className="space-y-2">
              {Object.entries(v.compliance.by_type).map(([k, s]) => <li key={k} className="flex items-center justify-between text-sm"><span>{titleCase(k === "puc" ? "PUC" : k === "tax" ? "Road tax" : k)}</span><StatusBadge status={s} testId={`overview-compliance-${k}`} /></li>)}
            </ul>
          </div>
          <div className="fv-card flex flex-col items-center p-5" data-testid="overview-fuel"><FuelGauge pct={v.fuel_level_pct} /><button onClick={() => setTab("fuel")} data-testid="overview-log-fuel" className="mt-2 text-sm font-medium text-primary hover:underline">Log fuel</button></div>
          <VehicleQR vehicle={v} />
        </div>
      )}
      {tab === "fuel" && <FuelTab vehicle={v} reload={load} />}
      {tab === "photos" && <PhotosTab vehicleId={v.id} />}
      {tab === "consumables" && <ResourceSection cfg={consumables} params={{ vehicle_id: v.id }} fixed={{ vehicle_id: v.id }} />}
      {tab === "compliance" && <ComplianceTab vehicle={v} reload={load} />}
      {tab === "odometer" && <OdometerTab vehicle={v} reload={load} />}
      {tab === "issues" && (
        <div className="space-y-3">
          <div className="flex justify-end"><button onClick={() => setIssueOpen(true)} data-testid="report-issue-btn" className="flex h-9 items-center gap-1 rounded-full bg-primary px-3 text-sm font-semibold text-primary-foreground hover:bg-[#0071e3]"><Plus className="h-4 w-4" />Report issue</button></div>
          <IssueList issues={v.issues} showVehicle={false} />
        </div>
      )}
      {tab === "services" && (
        <div className="space-y-3">
          {can("admin", "fleet_manager", "garage_supervisor") && <div className="flex justify-end"><button onClick={() => setSvcOpen(true)} data-testid="add-service-btn" className="flex h-9 items-center gap-1 rounded-full bg-primary px-3 text-sm font-semibold text-primary-foreground hover:bg-[#0071e3]"><Plus className="h-4 w-4" />Record service</button></div>}
          <ServiceList records={v.services} />
        </div>
      )}
      <VehicleFormDialog open={edit} onClose={() => setEdit(false)} vehicle={v} onSaved={load} />
      <IssueFormDialog open={issueOpen} onClose={() => setIssueOpen(false)} vehicleId={v.id} onSaved={(i) => navigate(`/issues/${i.id}`)} />
      <ServiceFormDialog open={svcOpen} onClose={() => setSvcOpen(false)} vehicleId={v.id} onSaved={load} />
    </div>
  );
}
